/**
 * Map Legend Component
 * Shows color coding legend for tracts (by TractType) and wells (by ENVWellStatus)
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { symbologyConfig } from '../config/gisConfig';

function MapLegend({ visibleLayers = {} }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('tracts');

  // Determine which layers are currently shown
  const showTracts = visibleLayers.tracts;
  const showWells = visibleLayers.wells;
  const showLeaks = visibleLayers.leaks;
  const showPipeRisk = visibleLayers.pipe_risk;
  const showPremises = visibleLayers.sensitive_premises;
  const showSensors = visibleLayers.sensors;
  const showCrews = visibleLayers.crews;

  // Auto-select tab based on visible layers (must be before any early return)
  React.useEffect(() => {
    if (showPipeRisk) setActiveTab('pipe_risk');
    else if (showLeaks) setActiveTab('leaks');
    else if (showCrews) setActiveTab('crews');
    else if (showSensors) setActiveTab('sensors');
    else if (showPremises) setActiveTab('premises');
    else if (showWells && !showTracts) setActiveTab('wells');
    else if (showTracts && !showWells) setActiveTab('tracts');
  }, [showTracts, showWells, showLeaks, showPipeRisk, showPremises, showSensors, showCrews]);

  const emissionsLayerVisible = showLeaks || showPipeRisk || showPremises || showSensors || showCrews;

  // If no relevant layers visible, don't show legend
  if (!showTracts && !showWells && !emissionsLayerVisible) {
    return null;
  }

  const tractLegendItems = [
    { type: 'F', ...symbologyConfig.tractTypes['F'] },
    { type: 'O', ...symbologyConfig.tractTypes['O'] },
    { type: 'N', ...symbologyConfig.tractTypes['N'] },
    { type: 'U', ...symbologyConfig.tractTypes['U'] },
    { type: 'Q', ...symbologyConfig.tractTypes['Q'] },
    { type: 'V', ...symbologyConfig.tractTypes['V'] }
  ];

  const wellLegendItems = [
    { status: 'PRODUCING', ...symbologyConfig.wellStatuses['PRODUCING'] },
    { status: 'DUC', ...symbologyConfig.wellStatuses['DUC'] },
    { status: 'DRILLING', ...symbologyConfig.wellStatuses['DRILLING'] },
    { status: 'PERMITTED', ...symbologyConfig.wellStatuses['PERMITTED'] },
    { status: 'COMPLETED', ...symbologyConfig.wellStatuses['COMPLETED'] },
    { status: 'DRILLED', ...symbologyConfig.wellStatuses['DRILLED'] },
    { status: 'INACTIVE', label: 'Inactive/Shut-in', color: '#6b7280' },
    { status: 'P & A', ...symbologyConfig.wellStatuses['P & A'] }
  ];

  return (
    <div className="map-legend">
      <div
        className="legend-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Layers size={16} />
        <span>Legend</span>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>

      {isExpanded && (
        <div className="legend-content">
          {/* Tab buttons - only show if multiple layers visible */}
          {(() => {
            const tabs = [];
            if (showTracts)    tabs.push({ id: 'tracts',    label: 'Tracts' });
            if (showWells)     tabs.push({ id: 'wells',     label: 'Wells' });
            if (showPipeRisk)  tabs.push({ id: 'pipe_risk', label: 'Pipe risk' });
            if (showLeaks)     tabs.push({ id: 'leaks',     label: 'Leaks' });
            if (showPremises)  tabs.push({ id: 'premises',  label: 'Premises' });
            if (showSensors)   tabs.push({ id: 'sensors',   label: 'Sensors' });
            if (showCrews)     tabs.push({ id: 'crews',     label: 'Crews' });
            return tabs.length > 1 ? (
              <div className="legend-tabs">
                {tabs.map(t => (
                  <button
                    key={t.id}
                    className={`legend-tab ${activeTab === t.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            ) : null;
          })()}

          {/* Tract Legend */}
          {(activeTab === 'tracts' && showTracts) && (
            <div className="legend-section">
              <div className="legend-title">Tract Type</div>
              <div className="legend-items">
                {tractLegendItems.map(item => (
                  <div key={item.type} className="legend-item">
                    <div
                      className="legend-color-box"
                      style={{
                        backgroundColor: item.fillColor,
                        borderColor: item.color
                      }}
                    />
                    <span className="legend-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Well Legend */}
          {(activeTab === 'wells' && showWells) && (
            <div className="legend-section">
              <div className="legend-title">Well Status</div>
              <div className="legend-items">
                {wellLegendItems.map(item => (
                  <div key={item.status} className="legend-item">
                    <div
                      className="legend-color-line"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="legend-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pipe Risk Legend (PM #3: standardize colors + tier meaning) */}
          {activeTab === 'pipe_risk' && showPipeRisk && (
            <div className="legend-section">
              <div className="legend-title">Pipe replacement priority</div>
              <div className="legend-items">
                <div className="legend-item"><div className="legend-color-line" style={{ backgroundColor: '#dc2626', height: 4 }} /><span className="legend-label">P1 / Critical — replace this quarter</span></div>
                <div className="legend-item"><div className="legend-color-line" style={{ backgroundColor: '#ea580c', height: 3 }} /><span className="legend-label">P2 / High — replace within 6 months</span></div>
                <div className="legend-item"><div className="legend-color-line" style={{ backgroundColor: '#ca8a04', height: 2 }} /><span className="legend-label">P3 / Medium — annual plan</span></div>
                <div className="legend-item"><div className="legend-color-line" style={{ backgroundColor: '#94a3b8', height: 2 }} /><span className="legend-label">Low — monitor</span></div>
              </div>
            </div>
          )}

          {/* Leaks Legend */}
          {activeTab === 'leaks' && showLeaks && (
            <div className="legend-section">
              <div className="legend-title">Leak severity</div>
              <div className="legend-items">
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }} /><span className="legend-label">Grade 3 — emergency (≤4 hr response)</span></div>
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#ea580c', borderColor: '#ea580c' }} /><span className="legend-label">Grade 2 — non-emergency (≤30 days)</span></div>
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#eab308', borderColor: '#eab308' }} /><span className="legend-label">Grade 1 — monitor</span></div>
              </div>
            </div>
          )}

          {/* Sensitive premises */}
          {activeTab === 'premises' && showPremises && (
            <div className="legend-section">
              <div className="legend-title">Sensitive premises</div>
              <div className="legend-items">
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#2563eb', borderColor: '#2563eb' }} /><span className="legend-label">School</span></div>
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }} /><span className="legend-label">Hospital</span></div>
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }} /><span className="legend-label">Other (daycare/senior living)</span></div>
              </div>
            </div>
          )}

          {/* Fenceline sensors */}
          {activeTab === 'sensors' && showSensors && (
            <div className="legend-section">
              <div className="legend-title">Fenceline sensors</div>
              <div className="legend-items">
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }} /><span className="legend-label">Triggered — elevated CH₄</span></div>
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }} /><span className="legend-label">Normal</span></div>
              </div>
            </div>
          )}

          {/* Crews */}
          {activeTab === 'crews' && showCrews && (
            <div className="legend-section">
              <div className="legend-title">Field crews</div>
              <div className="legend-items">
                <div className="legend-item"><div className="legend-color-box" style={{ backgroundColor: '#22d3ee', borderColor: '#0891b2' }} /><span className="legend-label">Field crew location</span></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MapLegend;
